import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPrimaryRole } from '../../utils/modulePermissions';
import { 
  FiHome, 
  FiBook, 
  FiLayers, 
  FiCalendar, 
  FiMail, 
  FiMenu, 
  FiX,
  FiChevronDown,
  FiMinimize2,
  FiMaximize2,
  FiLogIn,
  FiUserPlus,
  FiUser,
  FiLogOut,
  FiArchive,
  FiSettings,
  FiInbox,
  FiClock,
  FiTag,
  FiFileText,
  FiStar,
  FiHelpCircle
} from 'react-icons/fi';

/**
 * Layout principal de la aplicación
 * Incluye navegación responsive y modo compacto
 */
export default function MainLayout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [aplicacionesOpen, setAplicacionesOpen] = useState(false);
  const [historicidadOpen, setHistoricidadOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [primaryRole, setPrimaryRole] = useState('Cargando...');
  const [historicidadRoles, setHistoricidadRoles] = useState([]);

  // Cargar rol principal del usuario
  useEffect(() => {
    if (user) {
      getPrimaryRole(user).then(role => {
        setPrimaryRole(role);
      });
    } else {
      setPrimaryRole('Invitado');
    }
  }, [user]);

  // Cargar roles de historicidad del usuario
  useEffect(() => {
    const loadHistoricidadRoles = async () => {
      if (!user || !user.id) {
        setHistoricidadRoles([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/users/${user.id}/permissions`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          setHistoricidadRoles([]);
          return;
        }

        const data = await response.json();
        const permissions = Array.isArray(data.data) ? data.data : [];

        // Filtrar solo permisos del módulo historicidad que estén activos
        const roles = permissions
          .filter(p => p.moduleName === 'historicidad' && p.isActive)
          .map(p => p.roleName);

        setHistoricidadRoles(roles);
      } catch (error) {
        console.error('Error cargando roles de historicidad:', error);
        setHistoricidadRoles([]);
      }
    };

    loadHistoricidadRoles();
  }, [user?.id]);

  // Menú base para todos los usuarios
  const baseMenuItems = [
    {
      path: '/',
      label: 'Inicio',
      icon: FiHome,
      exact: true
    },
    {
      path: '/calendario',
      label: 'Calendario',
      icon: FiCalendar
    },
    {
      path: '/biblia-estudio',
      label: 'Estudio Bíblico',
      icon: FiBook
    },
    {
      path: '/estudios',
      label: 'Posts y Artículos',
      icon: FiFileText
    },
    {
      path: '/mandamientos',
      label: 'Mandamientos',
      icon: FiStar
    },
    {
      path: '/historicidad',
      label: 'Historicidad',
      icon: FiArchive
    },
    {
      path: '/contacto',
      label: 'Contacto',
      icon: FiMail
    },
    {
      path: '/faq',
      label: 'Preguntas Frecuentes',
      icon: FiHelpCircle
    }
  ];

  // Menús adicionales según el rol
  const getMenuItems = () => {
    const items = [...baseMenuItems];

    // Agregar opciones de administrador
    if (user?.rol === 'administrador') {
      items.splice(2, 0, {
        path: '/admin',
        label: 'Administración',
        icon: FiSettings,
        submenu: [
          { path: '/admin/users', label: 'Gestionar Usuarios' },
          { path: '/admin/posts', label: 'Gestionar Posts' },
          { path: '/admin/categorias', label: 'Gestionar Categorías' },
          { path: '/admin/flujo/configuracion', label: 'Configuración del Flujo' }
        ]
      });
    }

    // Agregar bandejas de workflow
    if (user?.rol === 'gestor') {
      items.splice(3, 0, {
        path: '/historicidad/gestor',
        label: 'Bandeja Gestor',
        icon: FiInbox
      });
      items.splice(4, 0, {
        path: '/historicidad/historial',
        label: 'Historial General',
        icon: FiClock
      });
    }

    if (user?.rol === 'validador') {
      items.splice(3, 0, {
        path: '/historicidad/validador',
        label: 'Bandeja Validador',
        icon: FiInbox
      });
      items.splice(4, 0, {
        path: '/historicidad/historial',
        label: 'Historial General',
        icon: FiClock
      });
    }

    if (user?.rol === 'editor') {
      items.splice(3, 0, {
        path: '/historicidad/editor',
        label: 'Bandeja Editor',
        icon: FiInbox
      });
      items.splice(4, 0, {
        path: '/historicidad/historial',
        label: 'Historial General',
        icon: FiClock
      });
    }

    // Agregar historial general también para administrador
    if (user?.rol === 'administrador') {
      items.splice(3, 0, {
        path: '/historicidad/historial',
        label: 'Historial General',
        icon: FiClock
      });
    }

    return items;
  };

  const menuItems = getMenuItems();

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setAplicacionesOpen(false);
    setHistoricidadOpen(false);
    setAdminOpen(false);
  };

  const toggleCompactMode = () => {
    setCompactMode(!compactMode);
  };

  const handleLogout = () => {
    logout();
    closeSidebar();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Overlay para móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${compactMode ? 'w-16' : 'w-64'}
          flex flex-col
        `}
      >
        {/* Header del Sidebar */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          {!compactMode && (
            <h1 className="text-xl font-bold text-blue-600">
              Shalom
            </h1>
          )}
          {compactMode && (
            <div className="flex items-center justify-center w-full">
              <FiHome className="w-6 h-6 text-blue-600" />
            </div>
          )}
        </div>

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);

              if (item.submenu) {
                // Determinar qué submenu es y su estado
                const isAdminMenu = item.label === 'Administración';
                const isApplicationsMenu = item.label === 'Aplicaciones';
                const submenuOpen = isAdminMenu ? adminOpen : (isApplicationsMenu ? aplicacionesOpen : false);
                const toggleSubmenu = () => {
                  if (isAdminMenu) {
                    setAdminOpen(!adminOpen);
                  } else if (isApplicationsMenu) {
                    setAplicacionesOpen(!aplicacionesOpen);
                  }
                };

                return (
                  <li key={item.path}>
                    <button
                      onClick={toggleSubmenu}
                      className={`
                        w-full flex items-center justify-between px-4 py-3 rounded-lg
                        transition-colors duration-150
                        ${active ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}
                        ${compactMode ? 'justify-center' : ''}
                      `}
                      title={compactMode ? item.label : ''}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {!compactMode && (
                          <span className="font-medium">{item.label}</span>
                        )}
                      </div>
                      {!compactMode && (
                        <FiChevronDown
                          className={`w-4 h-4 transition-transform ${
                            submenuOpen ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>

                    {/* Submenu */}
                    {submenuOpen && !compactMode && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {item.submenu.map((subitem) => (
                          <li key={subitem.path}>
                            <Link
                              to={subitem.path}
                              onClick={closeSidebar}
                              className={`
                                block px-4 py-2 rounded-lg text-sm
                                transition-colors duration-150
                                ${
                                  isActive(subitem.path)
                                    ? 'bg-blue-50 text-blue-600'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }
                              `}
                            >
                              {subitem.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={closeSidebar}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg
                      transition-colors duration-150
                      ${active ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}
                      ${compactMode ? 'justify-center' : ''}
                    `}
                    title={compactMode ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!compactMode && (
                      <span className="font-medium">{item.label}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer del Sidebar */}
        <div className="border-t border-gray-200">
          {/* Usuario o Login */}
          {user ? (
            <div className="p-3 border-b border-gray-200">
              {!compactMode ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <FiUser className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate" title={user.email}>
                        {user.email}
                      </p>
                      <p className="text-xs text-gray-500" title={primaryRole}>
                        {primaryRole}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiLogOut className="w-4 h-4" />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Cerrar Sesión"
                >
                  <FiLogOut className="w-5 h-5" />
                </button>
              )}
            </div>
          ) : (
            <div className="p-3 border-b border-gray-200">
              {!compactMode ? (
                <div className="space-y-2">
                  <Link
                    to="/login"
                    onClick={closeSidebar}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                  >
                    <FiLogIn className="w-4 h-4" />
                    <span>Iniciar Sesión</span>
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeSidebar}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <FiUserPlus className="w-4 h-4" />
                    <span>Registrarse</span>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link
                    to="/login"
                    onClick={closeSidebar}
                    className="w-full flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Iniciar Sesión"
                  >
                    <FiLogIn className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeSidebar}
                    className="w-full flex items-center justify-center p-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    title="Registrarse"
                  >
                    <FiUserPlus className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Botón de modo compacto */}
          <div className="p-2">
            <button
              onClick={toggleCompactMode}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
              title={compactMode ? 'Expandir menú' : 'Modo compacto'}
            >
              {compactMode ? (
                <FiMaximize2 className="w-5 h-5" />
              ) : (
                <>
                  <FiMinimize2 className="w-5 h-5" />
                  <span className="text-sm">Modo compacto</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Contenido Principal */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header móvil */}
        <header className="lg:hidden h-16 bg-white border-b border-gray-200 flex items-center px-4">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? (
              <FiX className="w-6 h-6 text-gray-700" />
            ) : (
              <FiMenu className="w-6 h-6 text-gray-700" />
            )}
          </button>
          <h1 className="ml-4 text-xl font-bold text-blue-600">
            Shalom
          </h1>
        </header>

        {/* Área de contenido */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}


